/**
 * Authorization Code + PKCE helpers (RFC 7636).
 *
 * The console is a public client: there is no client secret anywhere in this
 * repository and none may be added. The code verifier is the only proof of
 * possession, so it is generated with the platform CSPRNG and never logged.
 */

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function randomUrlSafeString(byteLength = 48): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return toBase64Url(bytes);
}

/** 43–128 characters of unreserved charset, per RFC 7636 §4.1. */
export function createCodeVerifier(): string {
  return randomUrlSafeString(64);
}

export async function createCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return toBase64Url(new Uint8Array(digest));
}

export type PkceTransaction = {
  state: string;
  nonce: string;
  codeVerifier: string;
  /** Where to land after the callback completes. */
  returnTo: string;
  /** Which provider started this transaction. */
  provider?: "google";
  /** The exact redirect URI sent to the provider; the token exchange repeats it. */
  redirectUri?: string;
  createdAt: number;
};

const TRANSACTION_KEY = "backchannel.tax.pkce";
const TRANSACTION_TTL_MS = 10 * 60 * 1000;

/**
 * The in-flight transaction lives in **sessionStorage**, not localStorage: it
 * must survive the provider redirect (a full document navigation) but must not
 * outlive the browser tab or leak into another tab's login. It holds no access
 * token — only the verifier, state and nonce, all single-use.
 */
export function saveTransaction(transaction: PkceTransaction): void {
  sessionStorage.setItem(TRANSACTION_KEY, JSON.stringify(transaction));
}

export function takeTransaction(): PkceTransaction | null {
  const raw = sessionStorage.getItem(TRANSACTION_KEY);
  sessionStorage.removeItem(TRANSACTION_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as PkceTransaction;
    if (
      typeof parsed.state !== "string" ||
      typeof parsed.codeVerifier !== "string" ||
      Date.now() - parsed.createdAt > TRANSACTION_TTL_MS
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearTransaction(): void {
  sessionStorage.removeItem(TRANSACTION_KEY);
}
